import React, { Fragment, useMemo, useState } from 'react';
import { Accordion } from '@components/Accordion';
import {
  RelatedTabsTypes,
  PageTypes,
  RelatedContent,
  RelatedDocuments,
  isRelatedDocuments,
} from './Related.types';
import {
  RelatedContainer,
  RelatedDocContainer,
  RelatedServiceContainer,
  RelatedProductModelContainer,
} from './components';
import { useFetchRelatedTabsQuery } from './Related.service';
import './Related.scss';

const Related = () => {
  const { data: relatedTabs } = useFetchRelatedTabsQuery();
  const getTabWithId = (id: string): RelatedContent => {
    return {
      id,
      ...relatedTabs[id],
    };
  };

  const filteredList = useMemo(() => {
    if (!relatedTabs) return [];

    const sortedList: RelatedContent[] = [];
    switch (window.pageType) {
      case PageTypes.PageIndustry:
      case PageTypes.PageProductConstruction: {
        if (!relatedTabs.RelatedProducts.HideRelatedTabs) {
          sortedList.push(getTabWithId(RelatedTabsTypes.RelatedProducts));
        }

        if (!relatedTabs.RelatedIndustries.HideRelatedTabs) {
          sortedList.push(getTabWithId(RelatedTabsTypes.RelatedIndustries));
        }

        if (!relatedTabs.RelatedServices.HideRelatedTabs) {
          sortedList.push(getTabWithId(RelatedTabsTypes.RelatedServices));
        }
        break;
      }

      case PageTypes.PageProductLine: {
        sortedList.push(getTabWithId(RelatedTabsTypes.RelatedDocuments));
        if (!relatedTabs.RelatedServices.HideRelatedTabs) {
          sortedList.push(getTabWithId(RelatedTabsTypes.RelatedServices));
        }

        sortedList.push({
          ...getTabWithId(RelatedTabsTypes.RelatedProductModel),
          /* Name is set to null to avoid accordion render */
          Name: relatedTabs.RelatedProductModel.HideRelatedTabs
            ? null
            : relatedTabs.RelatedProductModel.Name,
        });

        if (!relatedTabs.RelatedIndustries.HideRelatedTabs) {
          sortedList.push(getTabWithId(RelatedTabsTypes.RelatedIndustries));
        }
        break;
      }
    }
    return sortedList.filter(
      ({ Items, RenderedServiceContentArea }) =>
        (Items && Items.length > 0) || RenderedServiceContentArea
    );
  }, [relatedTabs]);

  const renderContainer = (tab: RelatedContent | RelatedDocuments) => {
    let styleType: string;

    switch (tab.id) {
      case RelatedTabsTypes.RelatedProducts:
        styleType = 'is-style-1';
        break;
      case RelatedTabsTypes.RelatedProductModel:
        styleType = 'is-style-1';
        break;
      case RelatedTabsTypes.RelatedIndustries:
        styleType = 'is-style-2';
        break;
      case RelatedTabsTypes.RelatedServices:
        styleType = 'is-style-3';
        break;
      default:
        styleType = 'is-style-3';
    }
    if (isRelatedDocuments(tab)) {
      return (
        <RelatedDocContainer
          styleType={styleType}
          content={tab.Items}
          customTabText={tab.CustomDocumentTabText}
          customTabContent={tab.CustomDocumentTabContentArea}
          I18n={relatedTabs.I18n}
          othersLabel={relatedTabs.OtherTabLocalizedName}
        />
      );
    } else if (tab.id === RelatedTabsTypes.RelatedServices) {
      const key = {
        '<iframe': '<iframe loading="lazy"',
        '<img': '<img loading="lazy"',
      };

      return (
        <RelatedServiceContainer
          styleType={styleType}
          content={tab.Items}
          contentStandard={tab.StandardPages}
          blockContent={tab.RenderedServiceContentArea?.replaceAll(
            /(<iframe|<img)/g,
            (char: string) => key[char] || ''
          )}
          I18n={relatedTabs.I18n}
          hideServiceList={relatedTabs.RelatedServices.HideServiceList}
          contentType={tab.id}
        />
      );
    } else if (tab.id === RelatedTabsTypes.RelatedProductModel) {
      return (
        <RelatedProductModelContainer
          content={relatedTabs.RelatedProductModel.Items.flatMap(
            (item) => item.TechnicalDataViewModels
          )}
          productLine={relatedTabs.RelatedProductModel.Items[0]}
          I18n={relatedTabs.I18n}
          styleType={styleType}
          hideProductModels={relatedTabs.RelatedProductModel.HideRelatedTabs}
        />
      );
    } else {
      return (
        <RelatedContainer
          styleType={styleType}
          content={tab.Items}
          blockContent={tab.RenderedServiceContentArea}
          I18n={relatedTabs.I18n}
          contentType={tab.id}
        />
      );
    }
  };

  return (
    <>
      {filteredList.map((tab, key) => {
        if (!tab.Name) return renderContainer(tab);

        return (
          <Fragment key={key}>
            <span id={`Nav-${tab.id}`} />
            <div id={tab.id}>
              <div className='c-new-related'>
                <Accordion.Container className='is-related'>
                  <Accordion.Section index={key} tab={{ name: tab.Name, id: tab.id }} url={null}>
                    {renderContainer(tab)}
                  </Accordion.Section>
                </Accordion.Container>
              </div>
            </div>
          </Fragment>
        );
      })}
    </>
  );
};

export default Related;
